/**
 * ANA-R1-09 (AC3) — inline "session ended" notice for a pop-out window.
 *
 * A pop-out widget window shares the main window's session for its whole
 * lifetime (token rotations propagate over the auth BroadcastChannel). It must
 * NEVER mount the login redirect flow — signing in from a pop-out would create
 * a second, parallel session. So when the main session genuinely ends (the
 * authority cleared/expired it and broadcast that), the pop-out shows this
 * small inline notice and offers to close itself, rather than navigating to
 * `/login`.
 */

import type { ReactElement } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

export function PopoutSessionEnded(): ReactElement {
  return (
    <Box
      role="alert"
      aria-label="Session ended"
      data-testid="popout-session-ended"
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 4,
        bgcolor: "background.default",
      }}
    >
      <Stack spacing={1.5} sx={{ maxWidth: 360, textAlign: "center" }}>
        <Typography variant="h6" component="p">
          Session ended
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your sign-in ended in the main window. Sign in there to keep working —
          this pop-out can be closed.
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={() => {
              try {
                window.close();
              } catch {
                /* some browsers refuse to close non-script-opened windows */
              }
            }}
          >
            Close window
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
